import type { Dispatch } from 'redux'

import {
  forgotPassword,
  postLoginToken,
  googleLogin,
  microsoftLogin,
  postSignUpToken,
  refreshToken,
  checkIfExistingUser,
  saveSignupInfo,
  getUserProfile,
} from 'api/AuthAPI'
import {
  authenticateError,
  authenticateLogIn,
  authenticateRequest,
  authLogout,
  getUserSettingsSuccess,
  getUserProfileSuccess,
} from './actions'
import type { UserCredentials } from './types'
import key from 'utils/localStorageKeys'
import { pushToDataLayer } from 'utils/eventPushes'
import { getUserSettings, updateUserSettings } from 'api/accountApi'
import { getAuth, signInWithCustomToken, type User } from 'firebase/auth'
import { trackEvent, boot } from '@intercom/messenger-js-sdk'
import { v4 as uuidv4 } from 'uuid'
import { setErrorMessage } from 'reduxStore/projects/actions'
import { IS_MOBILE_DEVICE } from 'hooks/useBreakpoint'
import { getGclid } from 'utils/attribution'

const INTERCOM_APP_ID = 'f53o132a'

// Firebase already created the account during sign-in; remove it so the rejection actually sticks.
const rejectNewUser = async (user: User, message: string, dispatch: Dispatch) => {
  await user.delete().catch(() => {})
  dispatch(authLogout())
  dispatch(setErrorMessage(message))
}

export const googleResponse =
  (response: any, fromSample: boolean, waitListOptedIn: boolean) => async (dispatch: Dispatch) => {
    if (response.error) {
      dispatch(authenticateError(response.error))
      return
    }

    dispatch(authenticateRequest())

    try {
      const res = await googleLogin(response)

      key.TOKEN = res.user.accessToken
      localStorage.setItem('token', key.TOKEN)

      const isNewUser = res.user.metadata.creationTime === res.user.metadata.lastSignInTime

      // Fingerprint check for new users
      if (isNewUser) {
        const { allowed } = await checkIfExistingUser(res.user.email)
        if (!allowed) {
          await rejectNewUser(
            res.user,
            'Cannot create account. Please contact support if you believe this is an error.',
            dispatch,
          )
          return
        }

        // if (localStorage.getItem('sessionId')) {
        //   await rejectNewUser(res.user, 'Cannot use this account. Please contact support.', dispatch)
        //   return
        // }
        localStorage.setItem('sessionId', uuidv4())
        pushToDataLayer({
          event: 'sign_up',
          method: 'google',
          success: true,
          current_user_id: res.user.uid,
          email: res.user.email,
          login_time: new Date().toISOString(),
          waitlist_opted_in: waitListOptedIn,
        })

        trackEvent('signed_up', {
          email: res.user.email,
          user_id: res.user.uid,
          created_at: Math.floor(new Date(res.user.metadata.creationTime).getTime() / 1000),
        })
      } else {
        pushToDataLayer({
          event: 'login',
          method: 'google',
          success: true,
          current_user_id: res.user.uid,
          email: res.user.email,
          login_time: new Date().toISOString(),
        })
        trackEvent('logged_in', {
          email: res.user.email,
          user_id: res.user.uid,
        })
      }

      if (!IS_MOBILE_DEVICE) {
        boot({
          app_id: INTERCOM_APP_ID,
          user_id: res.user.uid,
          email: res.user.email,
        })
      }

      dispatch(
        authenticateLogIn({
          uid: res.user.uid,
          login: res.user.email!,
          displayName: res.user.displayName,
          email: res.user.email!,
          activated: res.user.emailVerified!,
          newUser: isNewUser,
          lastLoginTime: res.user.metadata.lastSignInTime,
          lastLoginAt: res.user.metadata.lastLoginAt,
        }),
      )
      handleGetUserSettings()(dispatch)
      handleGetUserProfile()(dispatch)
      // isNewUser&&  await createSampleProject() // Create sample project for new user
    } catch ({ message }) {
      dispatch(authenticateError(message))
    }
  }

export const microsoftResponse = (_fromSample: boolean, waitListOptedIn?: boolean) => async (dispatch: Dispatch) => {
  dispatch(authenticateRequest())

  try {
    const res = await microsoftLogin()

    key.TOKEN = res.user.accessToken
    localStorage.setItem('token', key.TOKEN)

    const isNewUser = res.user.metadata.creationTime === res.user.metadata.lastSignInTime

    // Fingerprint check for new users
    if (isNewUser) {
      const { allowed } = await checkIfExistingUser(res.user.email)
      if (!allowed) {
        await rejectNewUser(
          res.user,
          'Cannot create account. Please contact support if you believe this is an error.',
          dispatch,
        )
        return
      }

      // if (localStorage.getItem('sessionId')) {
      //   await rejectNewUser(res.user, 'Cannot use this account. Please contact support.', dispatch)
      //   return
      // }
      localStorage.setItem('sessionId', uuidv4())
      pushToDataLayer({
        event: 'sign_up',
        method: 'microsoft',
        success: true,
        current_user_id: res.user.uid,
        email: res.user.email,
        login_time: new Date().toISOString(),
        waitlist_opted_in: waitListOptedIn,
      })

      trackEvent('signed_up', {
        email: res.user.email,
        user_id: res.user.uid,
        created_at: Math.floor(new Date(res.user.metadata.creationTime).getTime() / 1000),
      })
    } else {
      pushToDataLayer({
        event: 'login',
        method: 'microsoft',
        success: true,
        current_user_id: res.user.uid,
        email: res.user.email,
        login_time: new Date().toISOString(),
      })
      trackEvent('logged_in', {
        email: res.user.email,
        user_id: res.user.uid,
      })
    }

    if (!IS_MOBILE_DEVICE) {
      boot({
        app_id: INTERCOM_APP_ID,
        user_id: res.user.uid,
        email: res.user.email,
      })
    }

    dispatch(
      authenticateLogIn({
        uid: res.user.uid,
        login: res.user.email!,
        displayName: res.user.displayName,
        email: res.user.email!,
        activated: res.user.emailVerified!,
        newUser: isNewUser,
        lastLoginTime: res.user.metadata.lastSignInTime,
      }),
    )
    handleGetUserSettings()(dispatch)
    handleGetUserProfile()(dispatch)
    // isNewUser &&  await createSampleProject() // Create sample project for new user
  } catch ({ message }) {
    dispatch(authenticateError(message))
  }
}

export const handleForgotPassword = (email: string) => (_dispatch: Dispatch) => {
  return forgotPassword(email)
}

export const handleRefreshToken = () => (_dispatch: Dispatch) => {
  return refreshToken()
    .then((res) => {
      key.TOKEN = res
      if (res) {
        localStorage.setItem('token', res)
      }
    })
    .catch(({ message }) => {
      throw {
        success: false,
        message: message,
      }
    })
}

// Assuming postSignUpToken creates the Firebase user (which signs them in)
// and returns the Firebase user (cred.user) like in your previous message.

export const userSignUp =
  (user: UserCredentials & { username?: string }, waitListOptedIn?: boolean) => async (dispatch: Dispatch) => {
    dispatch(authenticateRequest())
    try {
      if (localStorage.getItem('sessionId')) {
        dispatch(authLogout())
        // dispatch(setErrorMessage('Cannot use multiple accounts.'))
        throw { success: false, message: 'Cannot create account. Please contact support.' }
      }
      const res = await postSignUpToken({
        email: user.email,
        password: user.password,
        username: (user as any).username,
      })

      // Prefer a fresh ID token instead of reading res.user.accessToken
      const idToken = await res.user.getIdToken()
      key.TOKEN = idToken
      localStorage.setItem('token', idToken)

      console.log('postSignUpToken response:', res)
      saveSignupInfo(
        {
          phone: (user as any).phonenumber,
          trade: (user as any).trade,
          fullName: (user as any).username,
          gclid: getGclid(),
        },
        { skipGlobalError: true },
      ).catch(() => {}) // Don't block signup if this fails

      localStorage.setItem('sessionId', uuidv4())
      const isMobile = window.innerWidth <= 768
      const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024
      if (!isMobile && !isTablet) {
        boot({
          app_id: INTERCOM_APP_ID,
          user_id: res.user.uid,
          email: user.email,
        })
      }

      trackEvent('signed_up', {
        email: user.email,
        user_id: res.user.uid,
        created_at: Math.floor(new Date(res.user.metadata.creationTime).getTime() / 1000),
      })

      pushToDataLayer({
        event: 'sign_up',
        method: 'email',
        success: true,
        current_user_id: res.user.uid,
        email: user.email,
        login_time: new Date().toISOString(),
        waitlist_opted_in: waitListOptedIn,
      })

      // Same state as login
      dispatch(
        authenticateLogIn({
          uid: res.user.uid,
          login: res.user.email!,
          displayName: res.user.displayName || (user as any).username || '',
          email: res.user.email!,
          activated: !!res.user.emailVerified, // likely false immediately after sign-up
          newUser: true,
        }),
      )
      handleGetUserSettings()(dispatch)
      handleGetUserProfile()(dispatch)

      // await createSampleProject() // Create sample project for new user

      return res
    } catch (e: any) {
      dispatch(authenticateError(e.message))
      throw { success: false, message: e.message }
    }
  }

export const handleGetUserSettings = () => (_dispatch: Dispatch) => {
  getUserSettings()
    .then((res) => {
      _dispatch(getUserSettingsSuccess(res.data))
    })
    .catch(() => {
      // Settings endpoint may 404 for new users / misconfigured envs / sample sessions.
      // Treat as "no saved settings" so downstream gates can proceed with empty state
      // instead of hanging on `seenPopups === null`.
      _dispatch(getUserSettingsSuccess({ seenPopups: {} }))
    })
}

// Fetch the signed-in user's trade + phone number and merge onto the user in redux. Field names are
// read defensively (backend may send phoneNumber / phonenumber / phone). Failure is non-fatal.
export const handleGetUserProfile = () => (_dispatch: Dispatch) => {
  getUserProfile()
    .then((res) => {
      const data = res?.data ?? {}
      _dispatch(
        getUserProfileSuccess({
          trade: data.trade,
          phoneNumber: data.phoneNumber ?? data.phonenumber ?? data.phone,
        }),
      )
    })
    .catch(() => {
      // Profile endpoint may be unavailable / 404 for some users — leave the user object as-is.
    })
}

export const handleUpdateUserSettings = (settings: any) => (_dispatch: Dispatch) => {
  return updateUserSettings(settings).then((res) => {
    _dispatch(getUserSettingsSuccess(res.data))
    return res
  })
}

// ── Legacy walkthrough key migration ─────────────────────────────────────────
// The auto-started linear walkthrough tour was now gone. The old `walkthrough_completed` and `walkthrough_step_*`
// keys in `seenPopups` are now dead data. This one-shot thunk clears them and
// records `walkthrough_v1_cleared: true` so the migration only runs once per
// user. Triggered on first EditMode mount after settings load.
const WALKTHROUGH_V1_CLEARED_KEY = 'walkthrough_v1_cleared'

export const clearLegacyWalkthroughKeys = () => (dispatch: Dispatch, getState: () => any) => {
  const current: Record<string, boolean> = (getState()?.auth?.seenPopups as Record<string, boolean>) || {}
  if (current[WALKTHROUGH_V1_CLEARED_KEY]) return Promise.resolve()

  const next: Record<string, boolean> = {}
  for (const [k, v] of Object.entries(current)) {
    // Drop every key the old engine wrote — both `walkthrough_completed` and
    // every per-step `walkthrough_step_*` flag. Anything else carries over.
    if (k === 'walkthrough_completed' || k.startsWith('walkthrough_step_')) continue
    next[k] = v
  }
  next[WALKTHROUGH_V1_CLEARED_KEY] = true

  try {
    localStorage.removeItem('WalkThroughApp')
  } catch {
    // ignore
  }

  // Optimistic local apply so subsequent reads see the cleaned map immediately.
  dispatch(getUserSettingsSuccess({ seenPopups: next }))

  return updateUserSettings({ seenPopups: next })
    .then((res) => {
      if (res?.data) dispatch(getUserSettingsSuccess(res.data))
    })
    .catch((err) => {
      // Non-fatal: the v1_cleared flag is already set locally so we won't loop
      // on next mount. On next genuine settings write the cleaned map will be
      // re-attempted. Sample/anon users with no backend simply stay cleaned in
      // memory for the session.
      console.warn('Failed to persist legacy walkthrough cleanup to backend:', err)
    })
}

export const userLogIn = (user: UserCredentials) => (dispatch: Dispatch) => {
  dispatch(authenticateRequest())

  return postLoginToken({
    email: user.email,
    password: user.password,
  })
    .then((res: any) => {
      const { uid, accessToken } = res.user
      key.TOKEN = accessToken
      localStorage.setItem('token', accessToken)

      pushToDataLayer({
        event: 'login',
        method: 'email',
        success: true,
        current_user_id: uid,
        email: res.user.email,
        login_time: new Date().toISOString(),
      })

      // logEvent(analytics, 'login', {
      // 	method: 'email',
      // 	success: true,
      // 	custom_user_id: uid,
      // 	login_time: new Date().toISOString()
      // })
      localStorage.setItem('sessionId', uuidv4())

      dispatch(
        authenticateLogIn({
          uid: uid,
          login: res.user.email!,
          displayName: res.user.displayName!,
          email: res.user.email!,
          activated: res.user.emailVerified!,
          lastLoginTime: res.user.metadata.lastSignInTime,
          lastLoginAt: res.user.metadata.lastLoginAt,
        }),
      )
      handleGetUserSettings()(dispatch)
      handleGetUserProfile()(dispatch)
      return res
    })
    .catch(({ code, message }) => {
      dispatch(authenticateError(message))
      throw {
        success: false,
        code,
        message,
      }
    })
}

export const loginWithCustomToken = (token: string) => (dispatch: Dispatch) => {
  dispatch(authenticateRequest())

  const auth = getAuth()
  // Exchange custom token for Firebase session
  signInWithCustomToken(auth, token)
    .then((res: any) => {
      const { uid, accessToken } = res.user
      key.TOKEN = accessToken
      localStorage.setItem('token', accessToken)

      pushToDataLayer({
        event: 'login',
        method: 'email',
        success: true,
        current_user_id: uid,
        email: res.user.email,
        login_time: new Date().toISOString(),
      })

      // logEvent(analytics, 'login', {
      // 	method: 'email',
      // 	success: true,
      // 	custom_user_id: uid,
      // 	login_time: new Date().toISOString()
      // })

      dispatch(
        authenticateLogIn({
          uid: uid,
          login: res.user.email!,
          displayName: res.user.displayName!,
          email: res.user.email!,
          activated: res.user.emailVerified!,
          // lastLoginTime: res.user.metadata.lastSignInTime,
          // lastLoginAt: res.user.metadata.lastLoginAt,
        }),
      )
      handleGetUserSettings()(dispatch)
      handleGetUserProfile()(dispatch)
      return res
    })
    .catch(({ message }) => {
      dispatch(authenticateError(message))
      throw {
        success: false,
        message: message,
      }
    })
}
